import useApiRequest from "@/lib/hooks/use-request";
import { formatCurrency, germanToBackendNumber } from "@/lib/hooks/utils";
import { ProductResponseDto } from "@/lib/interfaces/product";
import {
  ProductLineItemDetailsResponseDto,
  SaleDetailsResponseDto,
  UpdateProductLineItemRequestDto,
} from "@/lib/interfaces/sales";
import { Paginated, Pagination } from "@/lib/interfaces/utils";
import { Button } from "@/ui-components/ui/button";
import ConfirmationDialog from "@/ui-components/ui/confirmation-dialog";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/ui-components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { SearchableDropdown } from "@/ui-components/ui/searchable-dropdown";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui-components/ui/table";
import { zodResolver } from "@hookform/resolvers/zod";
import { TrashIcon } from "lucide-react";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const ProductManager = ({
  item,
  setReload,
}: {
  item: SaleDetailsResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showAddFirstProduct, setShowAddFirstProduct] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductLineItemDetailsResponseDto>();
  const { apiRequest } = useApiRequest();

  const fetchProducts = useCallback(
    async (pagination: Pagination, searchText: string): Promise<Paginated<ProductResponseDto>> => {
      try {
        let url = `settings/products/search?limit=${pagination.pageSize}&page=${pagination.pageIndex + 1}`;
        if (searchText) {
          url = `${url}&search=${searchText}`;
        }
        const response = await apiRequest<Paginated<ProductResponseDto>>(url, "GET");

        if (!response.data) {
          return {
            docs: [],
            hasNextPage: false,
            limit: pagination.pageSize,
            offset: pagination.pageIndex * pagination.pageSize,
            page: pagination.pageIndex,
            hasPrevPage: pagination.pageIndex > 0,
            count: 0,
            pageCount: 0,
          };
        }

        return response.data;
      } catch (error) {
        console.error("Error fetching persons:", error);
        return {
          docs: [],
          hasNextPage: false,
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
          page: pagination.pageIndex,
          hasPrevPage: pagination.pageIndex > 0,
          count: 0,
          pageCount: 0,
        };
      }
    },
    [apiRequest]
  );

  const renderSearchOption = useCallback(
    (product: ProductResponseDto) => ({
      label: product.title,
      value: product._id,
    }),
    []
  );

  const onSelectProduct = useCallback(
    async (value: string, title: string) => {
      await apiRequest<ProductResponseDto>(`sales/${item._id}/line-items`, "POST", {
        body: {
          belongsToProductId: value,
        },
        toast: { toastText: `${title} erfolgreich hinzugefügt` },
      });
      setReload((prev) => !prev);
    },
    [apiRequest, item._id, setReload]
  );

  const onRemove = async (productId: string) => {
    await apiRequest(`sales/${item._id}/line-items/${productId}`, "DELETE", {
      toast: { toastText: "Produkt erfolgreich entfernt" },
    });
    setReload((prev) => !prev);
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex flex-row justify-between items-center">
        <h2 className="text-2xl font-bold">Posten</h2>
        {item.products.length > 0 && item.isEditable && (
          <>
            {showAddProduct ? (
              <div className="w-fit flex flex-row gap-2">
                <SearchableDropdown
                  fetchData={fetchProducts}
                  renderOption={renderSearchOption}
                  onSelect={onSelectProduct}
                  placeholder="Produkt suchen"
                  createNewLabel=""
                />
                <Button onClick={() => setShowAddProduct(false)} variant="outline">
                  Abbrechen
                </Button>
              </div>
            ) : (
              <Button onClick={() => setShowAddProduct(true)}>Produkt hinzufügen</Button>
            )}
          </>
        )}
      </div>
      {item.products.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produkt</TableHead>
              <TableHead>
                <div className="flex flex-col">
                  <span className="font-bold">Produktpreis</span>
                  <span className="text-sm text-muted-foreground italic">inkl. MwSt.</span>
                </div>
              </TableHead>
              <TableHead>
                <div className="flex flex-col">
                  <span className="font-bold">Verkaufspreis</span>
                  <span className="text-sm text-muted-foreground italic">inkl. MwSt.</span>
                </div>
              </TableHead>
              <TableHead>
                <div className="flex flex-col">
                  <span className="font-bold">Montagekosten</span>
                  <span className="text-sm text-muted-foreground italic">inkl. MwSt.</span>
                </div>
              </TableHead>
              <TableHead>Menge</TableHead>
              <TableHead>
                <div className="flex flex-col">
                  <span className="font-bold">Gesamtpreis</span>
                  <span className="text-sm text-muted-foreground italic">inkl. MwSt.</span>
                </div>
              </TableHead>
              <TableHead>Grundabsicherung</TableHead>
              <TableHead>Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {item.products.map((product) => (
              <TableRow
                key={product._id}
                onClick={() => {
                  if (item.isEditable) {
                    setSelectedProduct(product);
                  }
                }}
                className={item.isEditable ? "cursor-pointer" : ""}
              >
                <TableCell>
                  <span className="font-bold">{product.title}</span>
                </TableCell>

                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-bold">{formatCurrency(product.productNetPrice)}</span>
                    <span className="text-sm text-muted-foreground italic">
                      {formatCurrency(product.productGrossPrice)}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-bold">{formatCurrency(product.netPrice)}</span>
                    <span className="text-sm text-muted-foreground italic">{formatCurrency(product.grossPrice)}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-bold">
                      {product.productNetInstallationCost
                        ? formatCurrency(product.productNetInstallationCost)
                        : "0,00 €"}
                    </span>
                    <span className="text-sm text-muted-foreground italic">
                      {product.productGrossInstallationCost
                        ? formatCurrency(product.productGrossInstallationCost)
                        : "0,00 €"}
                    </span>
                  </div>
                </TableCell>
                <TableCell>{product.amount}</TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-bold">{formatCurrency(product.totalNetPrice)}</span>
                    <span className="text-sm text-muted-foreground italic">
                      {formatCurrency(product.totalGrossPrice)}
                    </span>
                  </div>
                </TableCell>
                <TableCell>{product.isBasicSecurity ? "Ja" : "Nein"}</TableCell>
                <TableCell className="cursor-default" onClick={(e) => e.stopPropagation()}>
                  <ConfirmationDialog
                    title="Produkt entfernen"
                    description="Möchten Sie das Produkt wirklich entfernen?"
                    onConfirm={() => onRemove(product._id)}
                  >
                    <Button variant="destructive" size="icon" disabled={!item.isEditable}>
                      <TrashIcon />
                    </Button>
                  </ConfirmationDialog>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="flex flex-col items-center justify-center gap-2 mt-4">
          {item.isEditable ? (
            showAddFirstProduct ? (
              <SearchableDropdown
                fetchData={fetchProducts}
                renderOption={renderSearchOption}
                onSelect={onSelectProduct}
                placeholder="Produkt suchen"
              />
            ) : (
              <>
                <p>Noch keine Produkte hinzugefügt</p>
                <Button onClick={() => setShowAddFirstProduct(true)}>Produkt hinzufügen</Button>
              </>
            )
          ) : (
            <p>Produkte können aktuell nicht hinzugefügt werden</p>
          )}
        </div>
      )}
      <ProductDialog
        id={item._id}
        item={selectedProduct}
        setSelectedProduct={setSelectedProduct}
        setReload={setReload}
      />
    </div>
  );
};

const ProductDialog = ({
  id,
  item,
  setSelectedProduct,
  setReload,
}: {
  id: string;
  item?: ProductLineItemDetailsResponseDto;
  setSelectedProduct: Dispatch<SetStateAction<ProductLineItemDetailsResponseDto | undefined>>;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const [open, setOpen] = useState(item !== undefined);
  const { apiRequest } = useApiRequest();

  const formSchema = z.object({
    netPrice: z
      .string()
      .min(0, {
        message: "Preis ist erforderlich",
      })
      .refine((value) => /^(?:\d{1,3}(?:\.\d{3})*(?:,\d{1,2})?|\d+(?:,\d{1,2})?)$/.test(value), {
        message: "Ungültiges Zahlenformat. Beispiel: 2.000,50",
      }),
    amount: z
      .string()
      .min(1, {
        message: "Menge ist erforderlich",
      })
      .refine((value) => /^\d+$/.test(value), {
        message: "Menge muss eine ganze Zahl sein",
      }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      netPrice: item?.netPrice
        ? item.netPrice.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "",
      amount: item?.amount?.toString() || "",
    },
  });

  useEffect(() => {
    setOpen(item !== undefined);
  }, [item]);

  useEffect(() => {
    if (open && item) {
      form.reset({
        netPrice: item.netPrice.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        amount: item.amount.toString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, open]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const req: UpdateProductLineItemRequestDto = {
      netPrice: germanToBackendNumber(data.netPrice),
      amount: Number(data.amount),
    };

    await apiRequest(`sales/${id}/line-items/${item?._id}`, "PATCH", {
      body: req,
      toast: { toastText: "Produkt erfolgreich bearbeitet" },
    });
    setReload((prev) => !prev);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          setSelectedProduct(undefined);
        }
        setOpen(open);
      }}
    >
      <DialogContent className="sm:max-w-[600px]">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 h-full">
            <DialogHeader>
              <DialogTitle>Produktposten "{item?.title}" bearbeiten</DialogTitle>
            </DialogHeader>
            <div>
              <div className="flex flex-col gap-2">
                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-2">
                      <FormLabel className="text-[16px] font-bold">Menge</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="netPrice"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-2">
                      <FormLabel className="text-[16px] font-bold">Verkaufspreis Netto</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <DialogFooter className="flex gap-2 sm:justify-start">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                }}
              >
                Abbrechen
              </Button>
              <Button type="submit" className="w-full">
                Speichern
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductManager;
